/**
 * CR-6266: one KPI's target ranges in Admin Setup > Performance.
 *
 * A range replaces the KPI's default target between its dates (the end date is
 * optional), so a customer can raise the bar over time — e.g. OTD 80% from
 * Dec 2024, then 90% from Jan 2026. Dates no range covers keep the default.
 * Ranges for one KPI never overlap; the API refuses one that would and says
 * which range it clashes with.
 */

import { KPITargetKind, KPITargetRule } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useKpiTargetRules } from './useKpiTargetRules';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';

interface KpiTargetRulesTableProps {
  kpi: KPITargetKind;
  /** Display name for the KPI: "OTIF", "OTD", or the quality measure's label. */
  label: string;
  /** Unit after a target: '%' for OTIF / OTD, the quality measure (e.g. 'PPM') for quality. */
  unit: string;
}

export function KpiTargetRulesTable({ kpi, label, unit }: KpiTargetRulesTableProps) {
  const { formatDate } = useFormatters();
  const {
    rules, loading, loadError, reload, editing, draft, saving,
    startAdd, startEdit, cancel, changeDraft, save, remove,
  } = useKpiTargetRules(kpi, label);

  const isPercent = kpi !== 'ppm';
  const showTarget = (value: string) => `${Number(value)}${isPercent ? '%' : ` ${unit}`}`;
  const showEnd = (end: string | null) => (end ? formatDate(end) : 'No end date');
  const describe = (rule: KPITargetRule) =>
    `of ${showTarget(rule.target_value)} from ${formatDate(rule.effective_from)}`
    + (rule.effective_to ? ` to ${formatDate(rule.effective_to)}` : ' with no end date');

  const editorRow = (key: string) => (
    <TableRow key={key}>
      <TableCell>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            aria-label={`${label} target`}
            className="h-8 w-24"
            min="0"
            max={isPercent ? '100' : undefined}
            step={isPercent ? '0.01' : '1'}
            value={draft.target_value}
            onChange={(e) => changeDraft('target_value', e.target.value)}
          />
          <span className="text-sm text-muted-foreground">{isPercent ? '%' : unit}</span>
        </div>
      </TableCell>
      <TableCell>
        <Input type="date" aria-label={`${label} from`} className="h-8 w-40" value={draft.effective_from}
          onChange={(e) => changeDraft('effective_from', e.target.value)} />
      </TableCell>
      <TableCell>
        <Input type="date" aria-label={`${label} to (optional)`} className="h-8 w-40" value={draft.effective_to}
          onChange={(e) => changeDraft('effective_to', e.target.value)} />
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={cancel} disabled={saving}>
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
          Save
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium">{label} Target Ranges</p>
          <p className="text-xs text-muted-foreground">
            A range replaces the default {label} target between its dates. Dates no range covers use the default.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={startAdd} disabled={loading || !!loadError || editing !== null}
          aria-label={`Add ${label} target range`}>
          <Plus className="h-4 w-4 mr-2" />
          Add Range
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading {label} target ranges…
        </div>
      ) : loadError ? (
        <div className="flex items-center gap-2 py-3 text-sm text-red-600" role="alert">
          <AlertCircle className="h-4 w-4" />
          {loadError}
          <Button variant="link" size="sm" onClick={() => void reload()}>Retry</Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map((rule) => (editing === rule.id ? editorRow(`edit-${rule.id}`) : (
              <TableRow key={rule.id}>
                <TableCell className="font-medium">{showTarget(rule.target_value)}</TableCell>
                <TableCell>{formatDate(rule.effective_from)}</TableCell>
                <TableCell>{showEnd(rule.effective_to)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" aria-label={`Edit ${label} target range ${describe(rule)}`}
                    onClick={() => startEdit(rule)} disabled={editing !== null}>
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="sm" aria-label={`Delete ${label} target range ${describe(rule)}`}
                    onClick={() => void remove(rule, describe(rule))} disabled={editing !== null}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            )))}
            {editing === 'new' && editorRow('new')}
            {rules.length === 0 && editing !== 'new' && (
              <TableRow>
                <TableCell colSpan={4} className="py-4 text-center text-sm text-muted-foreground">
                  No target ranges — the default {label} target applies to every date.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
